import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { uploadPhoto } from "./uploadPhoto";
import NavBar from "./NavBar";

const API_BASE_URL = "https://api.itrustsecure.com";
function getToken() { return localStorage.getItem("itrust_token"); }
function authHeaders() { return { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` }; }

export default function SellerDashboardPage({ user, onLogout }) {
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [offers, setOffers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("listings");
  const [editingItem, setEditingItem] = useState(null);
  const [editQty, setEditQty] = useState("");
  const [savingQty, setSavingQty] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [listingTitle, setListingTitle] = useState("");
  const [listingDesc, setListingDesc] = useState("");
  const [listingMode, setListingMode] = useState("FIXED");
  const [listingCity, setListingCity] = useState("");
  const [listingState, setListingState] = useState("");
  const [items, setItems] = useState([{ title: "", price: "", quantity: "1", negotiable: false, condition: "GOOD", photos: [], uploading: false }]);
  const fileRefs = useRef([]);

  useEffect(() => {
    function handleResize() { setIsMobile(window.innerWidth < 768); }
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const loadData = useCallback(async () => {
    try {
      const [lr, or_, ord] = await Promise.all([
        fetch(`${API_BASE_URL}/listings`, { headers: authHeaders() }),
        fetch(`${API_BASE_URL}/offers`, { headers: authHeaders() }),
        fetch(`${API_BASE_URL}/orders`, { headers: authHeaders() }),
      ]);
      const ld = await lr.json(); const od = await or_.json(); const ordd = await ord.json();
      if (lr.ok) setListings((Array.isArray(ld) ? ld : []).filter(l => l.sellerId === user?.id));
      if (or_.ok) setOffers((Array.isArray(od) ? od : []).filter(o => o.sellerId === user?.id));
      if (ord.ok) setOrders((Array.isArray(ordd) ? ordd : []).filter(o => o.sellerId === user?.id));
    } catch (err) { console.warn(err); }
    finally { setLoading(false); }
  }, [user]);

  useEffect(() => { loadData(); }, [loadData]);

  async function handleAccept(offer) {
    if (!window.confirm(`Accept offer of $${Number(offer.totalAmount).toFixed(2)} from ${offer.buyer?.fullName}?`)) return;
    const res = await fetch(`${API_BASE_URL}/offers/${offer.id}/accept`, { method: "PUT", headers: authHeaders() });
    if (res.ok) { window.alert("Offer accepted! Buyer will now make payment."); loadData(); }
    else { const d = await res.json(); window.alert(d.error || "Could not accept offer"); }
  }

  async function handleReject(offer) {
    if (!window.confirm("Reject this offer?")) return;
    const res = await fetch(`${API_BASE_URL}/offers/${offer.id}/reject`, { method: "PUT", headers: authHeaders() });
    if (res.ok) { window.alert("Offer rejected."); loadData(); }
    else { const d = await res.json(); window.alert(d.error || "Could not reject offer"); }
  }

  function updateItem(index, field, value) {
    setItems(prev => prev.map((item, i) => i === index ? { ...item, [field]: value } : item));
  }
  function addItem() {
    setItems(prev => [...prev, { title: "", price: "", quantity: "1", negotiable: false, condition: "GOOD", photos: [], uploading: false }]);
  }
  function removeItem(index) { setItems(prev => prev.filter((_, i) => i !== index)); }

  function resetForm() {
    setListingTitle(""); setListingDesc(""); setListingMode("FIXED");
    setListingCity(""); setListingState("");
    setItems([{ title: "", price: "", quantity: "1", negotiable: false, condition: "GOOD", photos: [], uploading: false }]);
    setShowCreate(false);
  }

  async function handlePhotoUpload(index, e) {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    updateItem(index, "uploading", true);
    try {
      const urls = await Promise.all(files.map(f => uploadPhoto(f)));
      setItems(prev => prev.map((item, i) => i === index ? { ...item, photos: [...item.photos, ...urls], uploading: false } : item));
    } catch { window.alert("Photo upload failed."); updateItem(index, "uploading", false); }
    e.target.value = "";
  }

  function removePhoto(itemIndex, photoIndex) {
    setItems(prev => prev.map((item, i) => i === itemIndex ? { ...item, photos: item.photos.filter((_, pi) => pi !== photoIndex) } : item));
  }

  async function handleUpdateQuantity() {
    if (!editQty || Number(editQty) < 0) { window.alert("Enter a valid quantity"); return; }
    setSavingQty(true);
    try {
      const res = await fetch(`${API_BASE_URL}/listings/items/${editingItem.id}`, {
        method: "PUT", headers: authHeaders(),
        body: JSON.stringify({ availableQuantity: Number(editQty), totalQuantity: Math.max(Number(editQty), editingItem.totalQuantity) })
      });
      if (res.ok) { setEditingItem(null); setEditQty(""); loadData(); }
      else { const d = await res.json(); window.alert(d.error || "Could not update"); }
    } catch { window.alert("Something went wrong"); }
    finally { setSavingQty(false); }
  }

  async function handleCreateListing() {
    if (!listingTitle.trim()) { window.alert("Listing title is required"); return; }
    for (const item of items) {
      if (!item.title.trim()) { window.alert("All items need a title"); return; }
      if (!item.price || Number(item.price) <= 0) { window.alert("All items need a valid price"); return; }
      if (!item.quantity || Number(item.quantity) < 1) { window.alert("All items need a valid quantity"); return; }
    }
    setCreating(true);
    try {
      const listingRes = await fetch(`${API_BASE_URL}/listings`, {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify({ sellerId: user.id, title: listingTitle.trim(), description: listingDesc.trim() || null, pricingMode: listingMode, city: listingCity.trim() || null, state: listingState.trim() || null, status: "ACTIVE" })
      });
      const listingData = await listingRes.json();
      if (!listingRes.ok) { window.alert(listingData.error || "Could not create listing"); return; }
      for (const item of items) {
        const itemRes = await fetch(`${API_BASE_URL}/listings/items`, {
          method: "POST", headers: authHeaders(),
          body: JSON.stringify({ listingId: listingData.id, title: item.title.trim(), listedUnitPrice: Number(item.price), totalQuantity: Number(item.quantity), availableQuantity: Number(item.quantity), isNegotiable: item.negotiable, condition: item.condition || "GOOD", photoUrls: item.photos, status: "AVAILABLE" })
        });
        if (!itemRes.ok) { const d = await itemRes.json(); window.alert(d.error || "Could not create item"); return; }
      }
      resetForm();
      navigate(`/listing-detail/${listingData.id}`);
    } catch { window.alert("Something went wrong. Please try again."); }
    finally { setCreating(false); }
  }

  const pendingOffers = offers.filter(o => o.status === "PENDING");
  const completedOrders = orders.filter(o => o.status === "COMPLETED");
  const refundedOrders = orders.filter(o => o.status === "REFUNDED");
  const activeOrders = orders.filter(o => !["COMPLETED", "CANCELED", "REFUNDED"].includes(o.status));
  const totalEarned = completedOrders.reduce((sum, o) => sum + (Number(o.totalAmount) - Number(o.platformFee)), 0);
  const thisMonthEarned = completedOrders.filter(o => {
    const d = new Date(o.updatedAt); const n = new Date();
    return d.getMonth() === n.getMonth() && d.getFullYear() === n.getFullYear();
  }).reduce((sum, o) => sum + (Number(o.totalAmount) - Number(o.platformFee)), 0);

  const navItems = [
    { key: "listings", icon: "🛍️", label: "Listings", count: listings.length },
    { key: "offers", icon: "💬", label: "Offers", count: pendingOffers.length },
    { key: "sales", icon: "📦", label: "Sales", count: activeOrders.length },
    { key: "earnings", icon: "💰", label: "Earnings", count: null },
  ];

  const s = {
    root: { fontFamily: "'Segoe UI', Arial, sans-serif", background: "transparent", color: "#0f172a", minHeight: "100vh", position: "relative" },
    main: { maxWidth: 1100, margin: "0 auto", padding: isMobile ? "16px 12px" : "24px 20px" },
    statsBar: { display: "grid", gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(4, 1fr)", gap: isMobile ? 8 : 12, marginBottom: isMobile ? 16 : 20 },
    statCard: { background: "rgba(255,255,255,0.75)", border: "1px solid rgba(59,130,246,0.12)", borderRadius: 12, padding: isMobile ? "12px 14px" : "16px 18px", backdropFilter: "blur(8px)", boxShadow: "0 2px 8px rgba(59,130,246,0.08)" },
    statLabel: { fontSize: isMobile ? 10 : 11, color: "#64748b", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 },
    statValue: (color) => ({ fontSize: isMobile ? 22 : 26, fontWeight: 800, color }),
    statSub: { fontSize: 11, color: "#94a3b8", marginTop: 2 },
    tabBar: { display: "flex", gap: isMobile ? 4 : 8, marginBottom: isMobile ? 16 : 20, overflowX: "auto", paddingBottom: 4 },
    tabBtn: (active) => ({
      display: "flex", alignItems: "center", gap: 6,
      padding: isMobile ? "8px 12px" : "10px 18px",
      borderRadius: 10, border: "1px solid",
      borderColor: active ? "#3b82f6" : "rgba(59,130,246,0.15)",
      background: active ? "#3b82f6" : "rgba(255,255,255,0.7)",
      color: active ? "#fff" : "#475569",
      cursor: "pointer", fontWeight: 700,
      fontSize: isMobile ? 12 : 13,
      whiteSpace: "nowrap", flexShrink: 0,
    }),
    tabCount: (active) => ({
      background: active ? "rgba(255,255,255,0.25)" : "rgba(59,130,246,0.15)",
      borderRadius: 999, fontSize: 10, fontWeight: 800,
      padding: "1px 6px", color: active ? "#fff" : "#3b82f6",
    }),
    createBtn: { background: "#3b82f6", color: "#fff", border: "none", padding: isMobile ? "10px 16px" : "11px 20px", borderRadius: 10, cursor: "pointer", fontWeight: 700, fontSize: isMobile ? 13 : 14, marginBottom: 16, width: isMobile ? "100%" : "auto", boxShadow: "0 4px 12px rgba(59,130,246,0.25)" },
    card: { background: "rgba(255,255,255,0.75)", border: "1px solid rgba(59,130,246,0.12)", borderRadius: 14, padding: isMobile ? 14 : 18, marginBottom: 10, backdropFilter: "blur(8px)", boxShadow: "0 2px 8px rgba(59,130,246,0.06)" },
    saleRow: { display: "flex", justifyContent: "space-between", marginBottom: 5, fontSize: 13 },
    saleLabel: { color: "#64748b" },
    empty: { textAlign: "center", padding: isMobile ? "32px 16px" : "48px 20px", color: "#94a3b8" },
    listingStatus: (active) => ({ display: "inline-block", marginTop: 4, padding: "2px 7px", borderRadius: 999, fontSize: 9, fontWeight: 700, background: active ? "rgba(22,163,74,0.12)" : "rgba(107,114,128,0.12)", color: active ? "#16a34a" : "#6b7280" }),
    earningsGrid: { display: "grid", gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(4, 1fr)", gap: isMobile ? 8 : 12 },
    earningsCard: { background: "rgba(255,255,255,0.75)", border: "1px solid rgba(59,130,246,0.12)", borderRadius: 12, padding: isMobile ? "14px" : "18px", backdropFilter: "blur(8px)", boxShadow: "0 2px 8px rgba(59,130,246,0.06)" },
    earningsLabel: { fontSize: 10, color: "#64748b", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 },
    earningsValue: (color) => ({ fontSize: isMobile ? 22 : 26, fontWeight: 800, color }),
    overlay: { position: "fixed", inset: 0, background: "rgba(15,23,42,0.5)", zIndex: 200, overflowY: "auto", display: "flex", justifyContent: "center", padding: "20px 12px", backdropFilter: "blur(4px)" },
    modal: { background: "#fff", border: "1px solid rgba(59,130,246,0.15)", borderRadius: 20, padding: isMobile ? 20 : 28, width: "100%", maxWidth: 600, height: "fit-content", boxShadow: "0 20px 60px rgba(59,130,246,0.15)" },
    modalTitle: { fontSize: isMobile ? 20 : 22, fontWeight: 800, marginBottom: 6, color: "#0f172a" },
    modalSub: { fontSize: 13, color: "#64748b", marginBottom: 20 },
    label: { fontSize: 13, fontWeight: 600, color: "#475569", marginBottom: 5, display: "block" },
    input: { width: "100%", padding: "11px 13px", background: "rgba(59,130,246,0.04)", border: "1px solid rgba(59,130,246,0.2)", borderRadius: 10, color: "#0f172a", fontSize: 14, marginBottom: 12, boxSizing: "border-box", outline: "none" },
    textarea: { width: "100%", padding: "11px 13px", background: "rgba(59,130,246,0.04)", border: "1px solid rgba(59,130,246,0.2)", borderRadius: 10, color: "#0f172a", fontSize: 14, marginBottom: 12, boxSizing: "border-box", outline: "none", minHeight: 70, resize: "vertical" },
    select: { width: "100%", padding: "11px 13px", background: "#fff", border: "1px solid rgba(59,130,246,0.2)", borderRadius: 10, color: "#0f172a", fontSize: 14, marginBottom: 12, boxSizing: "border-box", outline: "none" },
    twoCol: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 },
    itemCard: { background: "rgba(59,130,246,0.03)", border: "1px solid rgba(59,130,246,0.1)", borderRadius: 12, padding: 12, marginBottom: 10 },
    itemHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 },
    itemNum: { fontSize: 12, fontWeight: 700, color: "#94a3b8" },
    removeItemBtn: { background: "rgba(220,38,38,0.08)", color: "#dc2626", border: "1px solid rgba(220,38,38,0.25)", padding: "3px 10px", borderRadius: 6, cursor: "pointer", fontSize: 11, fontWeight: 700 },
    checkRow: { display: "flex", alignItems: "center", gap: 8, marginBottom: 10 },
    checkbox: { width: 16, height: 16, cursor: "pointer", accentColor: "#3b82f6" },
    checkLabel: { fontSize: 13, color: "#475569", cursor: "pointer" },
    photoGrid: { display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 8 },
    photoThumb: { width: 60, height: 60, objectFit: "cover", borderRadius: 8, border: "1px solid rgba(59,130,246,0.15)" },
    photoRemove: { position: "absolute", top: -5, right: -5, background: "#ef4444", border: "none", borderRadius: "50%", width: 18, height: 18, color: "#fff", cursor: "pointer", fontSize: 9, fontWeight: 700 },
    uploadBtn: { padding: "7px 13px", background: "rgba(59,130,246,0.06)", border: "1px dashed rgba(59,130,246,0.3)", borderRadius: 8, color: "#3b82f6", cursor: "pointer", fontSize: 12, fontWeight: 600 },
    addItemBtn: { width: "100%", padding: "10px", background: "rgba(59,130,246,0.04)", border: "1px dashed rgba(59,130,246,0.2)", borderRadius: 10, color: "#64748b", cursor: "pointer", fontSize: 13, fontWeight: 600, marginBottom: 16 },
    modalActions: { display: "flex", gap: 10, marginTop: 4 },
    modalCancel: { flex: 1, background: "transparent", border: "1px solid rgba(59,130,246,0.2)", color: "#64748b", padding: "12px", borderRadius: 10, cursor: "pointer", fontWeight: 700, fontSize: 14 },
    modalSubmit: { flex: 2, background: "#3b82f6", color: "#fff", border: "none", padding: "12px", borderRadius: 10, cursor: "pointer", fontWeight: 700, fontSize: 14 },
  };

  if (loading) return (
    <div style={s.root}>
      <NavBar user={user} onLogout={onLogout} />
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "80vh", color: "#94a3b8" }}>Loading...</div>
    </div>
  );

  return (
    <div style={s.root}>
      <NavBar user={user} onLogout={onLogout} />
      <div style={s.main}>

        <div style={s.statsBar}>
          <div style={s.statCard}>
            <div style={s.statLabel}>Total Earned</div>
            <div style={s.statValue("#16a34a")}>${totalEarned.toFixed(0)}</div>
            <div style={s.statSub}>All time</div>
          </div>
          <div style={s.statCard}>
            <div style={s.statLabel}>This Month</div>
            <div style={s.statValue("#3b82f6")}>${thisMonthEarned.toFixed(0)}</div>
            <div style={s.statSub}>{new Date().toLocaleString("default", { month: "short" })}</div>
          </div>
          <div style={s.statCard}>
            <div style={s.statLabel}>Active Sales</div>
            <div style={s.statValue("#7c3aed")}>{activeOrders.length}</div>
            <div style={s.statSub}>In progress</div>
          </div>
          <div style={s.statCard}>
            <div style={s.statLabel}>Listings</div>
            <div style={s.statValue("#d97706")}>{listings.length}</div>
            <div style={s.statSub}>Total</div>
          </div>
        </div>

        <div style={s.tabBar}>
          {navItems.map(item => (
            <button key={item.key} style={s.tabBtn(tab === item.key)} onClick={() => setTab(item.key)}>
              <span>{item.icon}</span>
              <span>{item.label}</span>
              {item.count > 0 && <span style={s.tabCount(tab === item.key)}>{item.count}</span>}
            </button>
          ))}
        </div>

        {tab === "listings" && (
          <>
            <button style={s.createBtn} onClick={() => setShowCreate(true)}>+ Create New Listing</button>
            {listings.length === 0 ? (
              <div style={s.empty}>
                <div style={{ fontSize: 48, marginBottom: 12 }}>🛍️</div>
                <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8, color: "#475569" }}>No listings yet</div>
                <div style={{ fontSize: 13, marginBottom: 20 }}>Create your first listing to start selling</div>
              </div>
            ) : listings.map(listing => {
              const firstPhoto = listing.items?.find(i => i.photoUrls?.length > 0)?.photoUrls?.[0];
              const minPrice = listing.items?.length > 0 ? Math.min(...listing.items.map(i => Number(i.listedUnitPrice))) : 0;
              return (
                <div key={listing.id} style={{ ...s.card, cursor: "default", marginBottom: 12 }}>
                  <div style={{ display: "flex", gap: 12, marginBottom: 10, cursor: "pointer" }} onClick={() => navigate(`/listing-detail/${listing.id}`)}>
                    {firstPhoto ? (
                      <img src={firstPhoto} alt={listing.title} style={{ width: 54, height: 54, borderRadius: 10, objectFit: "cover", flexShrink: 0, border: "1px solid rgba(59,130,246,0.15)" }} onError={e => e.target.style.display = "none"} />
                    ) : (
                      <div style={{ width: 54, height: 54, borderRadius: 10, background: "rgba(59,130,246,0.08)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, flexShrink: 0 }}>🛍️</div>
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: "#1e293b" }}>{listing.title}</div>
                      <div style={{ fontSize: 11, color: "#94a3b8", marginBottom: 4 }}>
                        ${minPrice > 0 ? minPrice.toFixed(2) : "—"} · {new Date(listing.createdAt).toLocaleDateString()}
                        {listing.city ? ` · 📍 ${listing.city}` : ""}
                      </div>
                      <span style={s.listingStatus(listing.status === "ACTIVE")}>{listing.status}</span>
                    </div>
                  </div>
                  {listing.items?.map(item => (
                    <div key={item.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: "rgba(59,130,246,0.04)", borderRadius: 8, padding: "8px 12px", marginBottom: 5, fontSize: 13 }}>
                      <span style={{ color: "#475569", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, marginRight: 8 }}>{item.title}</span>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                        <span style={{ color: item.availableQuantity === 0 ? "#ef4444" : "#64748b", fontSize: 12 }}>{item.availableQuantity} avail</span>
                        <button
                          style={{ background: "rgba(59,130,246,0.08)", color: "#3b82f6", border: "1px solid rgba(59,130,246,0.25)", padding: "3px 10px", borderRadius: 6, cursor: "pointer", fontSize: 11, fontWeight: 700 }}
                          onClick={() => { setEditingItem(item); setEditQty(String(item.availableQuantity)); }}
                        >
                          Edit Qty
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })}
          </>
        )}

        {tab === "offers" && (
          <>
            <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 16, color: "#0f172a" }}>
              Offers {pendingOffers.length > 0 && `(${pendingOffers.length} pending)`}
            </div>
            {offers.length === 0 ? (
              <div style={s.empty}><div style={{ fontSize: 40, marginBottom: 12 }}>💬</div><div>No offers yet</div></div>
            ) : offers.map(offer => (
              <div key={offer.id} style={s.card}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 2, color: "#1e293b" }}>Offer from {offer.buyer?.fullName || "Buyer"}</div>
                    <div style={{ fontSize: 11, color: "#94a3b8" }}>#{offer.id.slice(0, 8)}</div>
                  </div>
                  <span style={{ padding: "4px 10px", borderRadius: 999, background: "rgba(217,119,6,0.1)", color: "#d97706", fontSize: 11, fontWeight: 700 }}>{offer.status}</span>
                </div>
                <div style={{ fontSize: 22, fontWeight: 800, color: "#1e40af", marginBottom: 10 }}>${Number(offer.totalAmount).toFixed(2)}</div>
                {offer.items?.map(item => (
                  <div key={item.id} style={{ display: "flex", justifyContent: "space-between", background: "rgba(59,130,246,0.04)", borderRadius: 8, padding: "7px 12px", marginBottom: 5, fontSize: 13 }}>
                    <span style={{ color: "#1e293b" }}>{item.listingItem?.title || "Item"}</span>
                    <span style={{ color: "#64748b" }}>{item.quantity} × ${Number(item.offeredUnitPrice).toFixed(2)}</span>
                  </div>
                ))}
                {offer.status === "PENDING" && (
                  <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                    <button style={{ background: "rgba(220,38,38,0.08)", color: "#dc2626", border: "1px solid rgba(220,38,38,0.25)", padding: "9px 16px", borderRadius: 8, cursor: "pointer", fontWeight: 700, fontSize: 13, flex: 1 }} onClick={() => handleReject(offer)}>Reject</button>
                    <button style={{ background: "#3b82f6", color: "#fff", border: "none", padding: "9px 16px", borderRadius: 8, cursor: "pointer", fontWeight: 700, fontSize: 13, flex: 1 }} onClick={() => handleAccept(offer)}>Accept</button>
                  </div>
                )}
              </div>
            ))}
          </>
        )}

        {tab === "sales" && (
          <>
            <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 16, color: "#0f172a" }}>Sales</div>
            {orders.length === 0 ? (
              <div style={s.empty}><div style={{ fontSize: 40, marginBottom: 12 }}>📦</div><div>No sales yet</div></div>
            ) : orders.map(order => (
              <div key={order.id} style={s.card}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, flex: 1, marginRight: 8, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: "#1e293b" }}>
                    {order.items?.map(i => i.listingItem?.title).filter(Boolean).join(", ") || "Order"}
                  </div>
                  <span style={{ padding: "3px 8px", borderRadius: 999, background: order.status === "COMPLETED" ? "rgba(22,163,74,0.12)" : order.status === "IN_PROGRESS" ? "rgba(124,58,237,0.12)" : "rgba(59,130,246,0.12)", color: order.status === "COMPLETED" ? "#16a34a" : order.status === "IN_PROGRESS" ? "#7c3aed" : "#3b82f6", fontSize: 10, fontWeight: 700, whiteSpace: "nowrap" }}>
                    {order.status === "IN_PROGRESS" ? "⏳ Awaiting" : order.status.replace(/_/g, " ")}
                  </span>
                </div>
                <div style={{ fontSize: 20, fontWeight: 800, color: "#1e40af", marginBottom: 10 }}>${Number(order.totalAmount).toFixed(2)}</div>
                <div style={s.saleRow}><span style={s.saleLabel}>Buyer</span><span style={{ color: "#1e293b" }}>{order.buyer?.fullName || "—"}</span></div>
                <div style={s.saleRow}><span style={s.saleLabel}>You Receive</span><span style={{ color: "#16a34a", fontWeight: 800 }}>${(Number(order.totalAmount) - Number(order.platformFee)).toFixed(2)}</span></div>
                <div style={s.saleRow}><span style={s.saleLabel}>Payment</span><span style={{ color: "#1e293b" }}>{order.payment ? "✅ Paid" : "⏳ Awaiting"}</span></div>
                {order.isInPerson && (
                  <div style={{ marginTop: 6, padding: "5px 10px", background: "rgba(59,130,246,0.08)", border: "1px solid rgba(59,130,246,0.2)", borderRadius: 8, fontSize: 11, color: "#3b82f6" }}>🤝 In-Person</div>
                )}
                {["FUNDED", "IN_PROGRESS"].includes(order.status) && !order.sellerConfirmed && (
                  <button style={{ marginTop: 10, background: "rgba(124,58,237,0.08)", color: "#7c3aed", border: "1px solid rgba(124,58,237,0.25)", padding: "9px 16px", borderRadius: 8, cursor: "pointer", fontWeight: 700, fontSize: 13, width: "100%" }} onClick={() => navigate(`/delivery/${order.id}`)}>
                    📦 Confirm Shipped
                  </button>
                )}
                {order.sellerConfirmed && !order.buyerConfirmed && (
                  <div style={{ marginTop: 8, padding: "8px 12px", background: "rgba(124,58,237,0.08)", border: "1px solid rgba(124,58,237,0.2)", borderRadius: 8, color: "#7c3aed", fontSize: 12, fontWeight: 700 }}>
                    ⏳ Waiting for buyer confirmation
                  </div>
                )}
              </div>
            ))}
          </>
        )}

        {tab === "earnings" && (
          <>
            <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 16, color: "#0f172a" }}>Earnings</div>
            <div style={s.earningsGrid}>
              <div style={s.earningsCard}>
                <div style={s.earningsLabel}>Total Earned</div>
                <div style={s.earningsValue("#16a34a")}>${totalEarned.toFixed(2)}</div>
                <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 4 }}>All time</div>
              </div>
              <div style={s.earningsCard}>
                <div style={s.earningsLabel}>This Month</div>
                <div style={s.earningsValue("#3b82f6")}>${thisMonthEarned.toFixed(2)}</div>
                <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 4 }}>{new Date().toLocaleString("default", { month: "long" })}</div>
              </div>
              <div style={s.earningsCard}>
                <div style={s.earningsLabel}>Completed</div>
                <div style={s.earningsValue("#7c3aed")}>{completedOrders.length}</div>
                <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 4 }}>Orders</div>
              </div>
              <div style={s.earningsCard}>
                <div style={s.earningsLabel}>Refunded</div>
                <div style={s.earningsValue("#dc2626")}>{refundedOrders.length}</div>
                <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 4 }}>Orders</div>
              </div>
            </div>
          </>
        )}
      </div>

      {editingItem && (
        <div style={s.overlay} onClick={() => setEditingItem(null)}>
          <div style={{ ...s.modal, maxWidth: 360 }} onClick={e => e.stopPropagation()}>
            <div style={s.modalTitle}>Update Availability</div>
            <div style={{ fontSize: 13, color: "#64748b", marginBottom: 20 }}>{editingItem.title}</div>
            <label style={s.label}>Available Quantity</label>
            <input style={s.input} type="number" min="0" value={editQty} onChange={e => setEditQty(e.target.value)} autoFocus onKeyDown={e => e.key === "Enter" && handleUpdateQuantity()} />
            <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 14 }}>
              Current: {editingItem.availableQuantity} available · {editingItem.totalQuantity} total
            </div>
            <div style={s.modalActions}>
              <button style={s.modalCancel} onClick={() => setEditingItem(null)}>Cancel</button>
              <button style={{ ...s.modalSubmit, opacity: savingQty ? 0.5 : 1 }} onClick={handleUpdateQuantity} disabled={savingQty}>
                {savingQty ? "Saving..." : "Update"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showCreate && (
        <div style={s.overlay} onClick={resetForm}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <div style={s.modalTitle}>Create New Listing</div>
            <div style={s.modalSub}>Fill in the details to list your items for sale</div>
            <label style={s.label}>Listing Title *</label>
            <input style={s.input} placeholder="e.g. Electronics Bundle" value={listingTitle} onChange={e => setListingTitle(e.target.value)} />
            <label style={s.label}>Description (Optional)</label>
            <textarea style={s.textarea} placeholder="Describe your listing..." value={listingDesc} onChange={e => setListingDesc(e.target.value)} />
            <div style={s.twoCol}>
              <div><label style={s.label}>City</label><input style={s.input} placeholder="e.g. Miami" value={listingCity} onChange={e => setListingCity(e.target.value)} /></div>
              <div><label style={s.label}>State</label><input style={s.input} placeholder="FL" maxLength={2} value={listingState} onChange={e => setListingState(e.target.value.toUpperCase())} /></div>
            </div>
            <label style={s.label}>Pricing Mode</label>
            <select style={s.select} value={listingMode} onChange={e => setListingMode(e.target.value)}>
              <option value="FIXED">Fixed Price</option>
              <option value="NEGOTIABLE">Negotiable</option>
              <option value="MIXED">Mixed</option>
            </select>
            <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 10, color: "#1e293b" }}>Items</div>
            {items.map((item, index) => (
              <div key={index} style={s.itemCard}>
                <div style={s.itemHeader}>
                  <span style={s.itemNum}>Item {index + 1}</span>
                  {items.length > 1 && <button style={s.removeItemBtn} onClick={() => removeItem(index)}>Remove</button>}
                </div>
                <label style={s.label}>Name *</label>
                <input style={s.input} placeholder="e.g. iPhone 14 Pro" value={item.title} onChange={e => updateItem(index, "title", e.target.value)} />
                <div style={s.twoCol}>
                  <div><label style={s.label}>Price ($) *</label><input style={s.input} type="number" step="0.01" min="0.01" placeholder="0.00" value={item.price} onChange={e => updateItem(index, "price", e.target.value)} /></div>
                  <div><label style={s.label}>Qty *</label><input style={s.input} type="number" min="1" placeholder="1" value={item.quantity} onChange={e => updateItem(index, "quantity", e.target.value)} /></div>
                </div>
                <label style={s.label}>Condition *</label>
                <select style={s.select} value={item.condition || "GOOD"} onChange={e => updateItem(index, "condition", e.target.value)}>
                  <option value="NEW">🌟 New</option>
                  <option value="LIKE_NEW">✨ Like New</option>
                  <option value="GOOD">👍 Good</option>
                  <option value="FAIR">⚠️ Fair</option>
                  <option value="POOR">🔧 Poor</option>
                </select>
                <div style={s.checkRow}>
                  <input type="checkbox" style={s.checkbox} id={`neg-${index}`} checked={item.negotiable} onChange={e => updateItem(index, "negotiable", e.target.checked)} />
                  <label htmlFor={`neg-${index}`} style={s.checkLabel}>Allow price negotiation</label>
                </div>
                <label style={s.label}>Photos</label>
                {item.photos.length > 0 && (
                  <div style={s.photoGrid}>
                    {item.photos.map((url, pi) => (
                      <div key={pi} style={{ position: "relative" }}>
                        <img src={url} alt="" style={s.photoThumb} />
                        <button style={s.photoRemove} onClick={() => removePhoto(index, pi)}>✕</button>
                      </div>
                    ))}
                  </div>
                )}
                {item.uploading ? (
                  <div style={{ fontSize: 12, color: "#3b82f6", marginBottom: 8 }}>⏳ Uploading...</div>
                ) : (
                  <>
                    <div style={s.uploadBtn} onClick={() => fileRefs.current[index]?.click()}>📷 Add Photos</div>
                    <input ref={el => fileRefs.current[index] = el} type="file" accept="image/*" multiple style={{ display: "none" }} onChange={e => handlePhotoUpload(index, e)} />
                  </>
                )}
              </div>
            ))}
            <button style={s.addItemBtn} onClick={addItem}>+ Add Another Item</button>
            <div style={s.modalActions}>
              <button style={s.modalCancel} onClick={resetForm}>Cancel</button>
              <button style={{ ...s.modalSubmit, opacity: creating ? 0.5 : 1 }} onClick={handleCreateListing} disabled={creating}>
                {creating ? "Creating..." : "Create Listing"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}